import { css, keyframes, SerializedStyles } from '@emotion/react';
import '@emotion/react';
import { mix, darken, transparentize } from 'polished';
import createTheme from '@mui/material/styles/createTheme';

const black = '#202328';
const white = '#FFFFFF';

const generatePalette = ({ color, background, foreground }: { color: string; background: string; foreground: string }) => ({
  100: color,
  80: mix(0.4, color, background),
  60: mix(0.15, color, background),
  text: mix(0.3, color, foreground),
});

export const generateShadow = ({ scale = 3, primary, black: blackProp = black }: { scale?: number; primary: string; black?: string }) => {
  return `${transparentize(0.9, primary)} 0px ${scale * 5}px ${scale * 10}px -${scale * 2}px, ${transparentize(0.4, blackProp)} 0px ${
    scale * 3
  }px ${scale * 6}px -${scale * 3}px;`;
};

const darkColor = {
  primary: generatePalette({ color: '#6650FA', background: black, foreground: white }),
  secondary: generatePalette({ color: '#EC609C', background: black, foreground: white }),
  green: generatePalette({ color: '#319A6E', background: black, foreground: white }),
  yellow: generatePalette({ color: '#ECA946', background: black, foreground: white }),
  red: generatePalette({ color: '#D05353', background: black, foreground: white }),

  grey: {
    100: '#B6BDC8',
    80: '#5A606C',
    60: '#31363F',
  },

  gold: '#F0D64D',

  black,
  white,

  foreground: white,
  background: black,
};

const lightColor = {
  primary: generatePalette({ color: '#6650FA', background: white, foreground: black }),
  secondary: generatePalette({ color: '#EC609C', background: white, foreground: black }),
  green: generatePalette({ color: '#319A6E', background: white, foreground: black }),
  yellow: generatePalette({ color: '#ECA946', background: white, foreground: black }),
  red: generatePalette({ color: '#D05353', background: white, foreground: black }),

  grey: {
    100: '#5A606C',
    80: '#B6BDC7',
    60: '#F5F7FA',
  },

  gold: '#F0D64D',

  black,
  white,

  foreground: black,
  background: white,
};

export const Color = darkColor;

export const Font = {
  header: `'Soleil', sans-serif`,
  body: `'Source Sans Pro', sans-serif`,
  code: `ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace`,
};

export const Easing = {
  basic: 'cubic-bezier(0.0, 0, 0.2, 1)',
  springy: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
};

export const Transition = {
  basic: `100ms ${Easing.basic} 0ms`,
};

export const spinAnimation = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

export const breakPoint = {
  large: 1280,
  medium: 960,
  small: 600,
};

export const mainWidth = 65;
export const sideWidth = 40;
export const gutterWidth = 10;
export const fullWidth = mainWidth + sideWidth + gutterWidth;

export const borderRadius = 6;
export const navbarHeight = 66;

/**
 * Shorthand to use media queries in CSS in JS
 *
 * NOTE: These are mobile first, build the component to look good on mobile,
 *       then use mobile queries to make it look good on desktop
 */
export const mediaQuery = {
  xlarge: (style: SerializedStyles) => css`
    @media (min-width: ${breakPoint.large}px) {
      ${style}
    }
  `,
  large: (style: SerializedStyles) => css`
    @media (min-width: ${breakPoint.medium}px) {
      ${style}
    }
  `,
  medium: (style: SerializedStyles) => css`
    @media (min-width: ${breakPoint.small}px) {
      ${style}
    }
  `,
};

export const layoutStyles = css`
  margin: 0 auto;
  width: 100%;
  max-width: ${fullWidth}ch;
`;

export const applyEffect = (color: string, effect: 'hover') => {
  switch (effect) {
    case 'hover':
      return darken(0.05, color);
  }
};

/** Material UI theme */
export const muiTheme = createTheme({
  palette: {
    primary: {
      main: Color.primary[100],
    },
    secondary: {
      main: Color.primary[100],
    },
  },
  typography: {
    fontFamily: Font.body,

    subtitle1: {
      color: Color.grey[100],
    },

    h3: {
      fontFamily: Font.header,
      fontWeight: 500,
    },
    h6: {
      fontFamily: Font.header,
    },
  },
});

const baseTheme = {
  font: Font,
  easing: Easing,
  transition: Transition,
  borderRadius,
  mediaQuery,
};

export const darkTheme = {
  type: 'dark' as 'dark' | 'light',
  color: darkColor,
  ...baseTheme,
};

export const lightTheme = {
  type: 'light' as 'dark' | 'light',
  color: lightColor,
  ...baseTheme,
};

type ThemeType = typeof darkTheme;

declare module '@emotion/react' {
  export interface Theme extends ThemeType {}
}
